import { lazy, Suspense } from 'react';

import { Routes } from '@datadog/browser-rum-react/react-router-v6';
import { RedirectToLogin, RequiredAuthProvider } from '@propelauth/react';
import { QueryClient, QueryClientProvider, setLogger } from 'react-query';
import { BrowserRouter, Navigate, Outlet, Route } from 'react-router-dom';

import { EnvVars } from '../environment';
import { getFrontendURLRegion } from './utils/checkFrontendURL';
import { Permission } from '../../backend/src/auth/permissions/permission';
import { ApiError } from '@/api/types';
import { UserProvider } from '@/Contexts/User';
import { BlockedAccountDialog } from '@/new/components/blocked-account-dialog';
import { ErrorBoundary } from '@/new/components/error-boundary';
import { ExportReadyDialog } from '@/new/components/export-ready-dialog';
import { Icon } from '@/new/components/icon';
import { MfaNonSSOGate } from '@/new/components/mfa-gate';
import { RelativityCallback } from '@/new/components/oauth/RelativityCallback';
import { PlatformPermissionRoute } from '@/new/components/platform-permission-route';
import { PracticeAreaGate } from '@/new/components/practice-area-gate';
import { TooltipProvider } from '@/new/components/shadcn/tooltip';
import { TermsOfUseGate } from '@/new/components/terms-of-use-gate';
import { Toaster } from '@/new/components/toaster';
import { AcceptTerms } from '@/new/pages/accept-terms';
import { LlmPricing } from '@/new/pages/admin/llm-pricing';
import { Admin } from '@/new/pages/admin/organisations';
import { AdminManageOrganisation } from '@/new/pages/admin/organisations/_organisationId';
import { OrganisationFeedback } from '@/new/pages/admin/organisations/_organisationId/feedback';
import { OrganisationRuns } from '@/new/pages/admin/organisations/_organisationId/runs';
import { AdminManageUsers } from '@/new/pages/admin/organisations/_organisationId/users';
import { AllRuns } from '@/new/pages/admin/runs';
import { Layout } from '@/new/pages/layout';
import { ManageUsers } from '@/new/pages/manage-users';
import { Matters } from '@/new/pages/matters';
import { MatterOverview } from '@/new/pages/matters/_matterId';
import { DocumentUpload } from '@/new/pages/matters/_matterId/docs/upload';
import { ProceduralTimeline } from '@/new/pages/matters/_matterId/procedural-timeline';
import { MatterSettings } from '@/new/pages/matters/_matterId/settings';
import { Subsets } from '@/new/pages/matters/_matterId/subsets';
import { SubsetOverview } from '@/new/pages/matters/_matterId/subsets/_subsetId';
import { Chronologies } from '@/new/pages/matters/_matterId/subsets/_subsetId/chronologies';
import { Chronology } from '@/new/pages/matters/_matterId/subsets/_subsetId/chronologies/:chronologyId';
import { DocChronologies } from '@/new/pages/matters/_matterId/subsets/_subsetId/chronologies/doc';
import { DocChronology } from '@/new/pages/matters/_matterId/subsets/_subsetId/chronologies/doc/:docChronologyId';
import { Documents } from '@/new/pages/matters/_matterId/subsets/_subsetId/docs';
import { Document } from '@/new/pages/matters/_matterId/subsets/_subsetId/docs/:docId';
import { SubsetEditor } from '@/new/pages/matters/_matterId/subsets/_subsetId/edit';
import { Entities } from '@/new/pages/matters/_matterId/subsets/_subsetId/entities';
import { Facts } from '@/new/pages/matters/_matterId/subsets/_subsetId/facts';
import { Inconsistencies } from '@/new/pages/matters/_matterId/subsets/_subsetId/inconsistencies';
import { InconsistencyPage } from '@/new/pages/matters/_matterId/subsets/_subsetId/inconsistencies/:inconsistencyId';
import { NewKim } from '@/new/pages/matters/_matterId/subsets/_subsetId/new-kim';
import { Realtime } from '@/new/pages/matters/_matterId/subsets/_subsetId/realtime';
import { Transcript } from '@/new/pages/matters/_matterId/subsets/_subsetId/realtime/_transcriptId';
import { SubsetCreator } from '@/new/pages/matters/_matterId/subsets/new';
import { MatterCreator } from '@/new/pages/matters/new/_matterId/index';
import { OrganisationSettings } from '@/new/pages/organisation-settings';
import { ReleaseNotes } from '@/new/pages/release-notes';
import { UserSettings } from '@/new/pages/settings';
import { Usage } from '@/new/pages/usage';
import { RedirectToNewFrontend } from '@/utils/redirectToNewFrontend';

// The help centre bundles every article's markdown and a search index, so it stays out of the main chunk.
const HelpCentre = lazy(() => import('@/new/pages/help-centre').then((module) => ({ default: module.HelpCentre })));
const HelpCentreArticle = lazy(() =>
  import('@/new/pages/help-centre/_articleId').then((module) => ({ default: module.HelpCentreArticle })),
);

const queryClient = new QueryClient();

// Expected client errors (4xx) are handled by the UI.
setLogger({
  log: console.log,
  warn: console.warn,
  error: (error) => {
    if (error instanceof ApiError && error.code < 500) {
      console.warn(error);
      return;
    }
    console.error(error);
  },
});

function App() {
  return (
    <ErrorBoundary>
      <RequiredAuthProvider
        authUrl={EnvVars.AUTH_URL}
        displayWhileLoading={
          <div className="flex h-screen w-screen items-center justify-center">
            <Icon className="lucide--loader animate-spin text-gray-500 ease-in-out" />
          </div>
        }
        displayIfLoggedOut={<RedirectToLogin postLoginRedirectUrl={EnvVars.FRONTEND_URL} />}
      >
        <QueryClientProvider client={queryClient}>
          <UserProvider>
            <MfaNonSSOGate>
              <BrowserRouter>
                <TooltipProvider>
                  <Toaster />
                  <BlockedAccountDialog />
                  <ExportReadyDialog />
                  <TermsOfUseGate />

                  <Routes>
                    <Route path="oauth/relativity/callback" element={<RelativityCallback />} />
                    <Route
                      element={
                        <PracticeAreaGate>
                          <Outlet />
                        </PracticeAreaGate>
                      }
                    >
                      <Route path="accept-terms" element={<AcceptTerms />} />

                      <Route path="/" element={<Layout />}>
                        <Route index element={<Navigate to="/matters" replace />} />
                        <Route path="settings" element={<UserSettings />} />
                        <Route path="release-notes" element={<ReleaseNotes />} />

                        <Route
                          path="help-centre"
                          element={
                            <Suspense
                              fallback={
                                <div className="flex h-full w-full items-center justify-center">
                                  <Icon className="lucide--loader animate-spin text-gray-500 ease-in-out" />
                                </div>
                              }
                            >
                              <Outlet />
                            </Suspense>
                          }
                        >
                          <Route index element={<HelpCentre />} />
                          <Route path=":articleId" element={<HelpCentreArticle />} />
                        </Route>

                        <Route path="usage/*" element={<Usage />} />
                        <Route path="manage-users" element={<ManageUsers />} />
                        <Route path="organisation-settings/*" element={<OrganisationSettings />} />

                        <Route path="admin">
                          <Route element={<PlatformPermissionRoute permissions={[Permission.PlatformManageRuns]} />}>
                            <Route path="runs" element={<AllRuns />} />
                          </Route>
                          <Route
                            element={<PlatformPermissionRoute permissions={[Permission.PlatformManageLlmPricing]} />}
                          >
                            <Route path="llm-pricing" element={<LlmPricing />} />
                          </Route>

                          <Route path="organisations">
                            <Route
                              element={
                                <PlatformPermissionRoute
                                  permissions={[
                                    Permission.PlatformManageOrganisations,
                                    Permission.PlatformDeleteOrganisations,
                                    Permission.PlatformManageBilling,
                                    Permission.PlatformManageUsers,
                                    Permission.PlatformViewUsage,
                                    Permission.PlatformManageRuns,
                                    Permission.PlatformViewFeedback,
                                  ]}
                                />
                              }
                            >
                              <Route index element={<Admin />} />
                            </Route>

                            <Route path=":organisationId">
                              <Route
                                element={
                                  <PlatformPermissionRoute
                                    permissions={[
                                      Permission.PlatformManageOrganisations,
                                      Permission.PlatformManageBilling,
                                    ]}
                                  />
                                }
                              >
                                <Route index element={<AdminManageOrganisation />} />
                              </Route>
                              <Route
                                element={<PlatformPermissionRoute permissions={[Permission.PlatformViewFeedback]} />}
                              >
                                <Route path="feedback" element={<OrganisationFeedback />} />
                              </Route>
                              <Route
                                element={<PlatformPermissionRoute permissions={[Permission.PlatformManageUsers]} />}
                              >
                                <Route path="users" element={<AdminManageUsers />} />
                              </Route>
                              <Route
                                element={<PlatformPermissionRoute permissions={[Permission.PlatformManageRuns]} />}
                              >
                                <Route path="runs" element={<OrganisationRuns />} />
                              </Route>
                            </Route>
                          </Route>
                        </Route>

                        <Route path="matters">
                          <Route index element={<Matters />} />
                          <Route path="new/:matterId" element={<MatterCreator />} />

                          <Route path=":matterId" element={<Outlet />}>
                            <Route index element={<MatterOverview />} />

                            <Route path="docs">
                              <Route path="upload" element={<DocumentUpload />} />
                            </Route>

                            <Route path="procedural-timeline" element={<ProceduralTimeline />} />

                            <Route path="settings/*" element={<MatterSettings />} />

                            <Route path="subsets">
                              <Route index element={<Subsets />} />
                              <Route path="new" element={<SubsetCreator />} />

                              <Route path=":subsetId">
                                <Route path="edit" element={<SubsetEditor />} />
                                <Route index element={<SubsetOverview />} />
                                <Route path="facts" element={<Facts />} />

                                <Route path="chronologies">
                                  <Route index element={<Chronologies />} />
                                  <Route path=":chronologyId" element={<Chronology />} />
                                  <Route path="doc" element={<DocChronologies />} />
                                  <Route path="doc/:docChronologyId" element={<DocChronology />} />
                                </Route>

                                <Route path="docs">
                                  <Route index element={<Documents />} />
                                  <Route path=":docId" element={<Document />} />
                                </Route>

                                <Route path="inconsistencies">
                                  <Route index element={<Inconsistencies />} />
                                  <Route path=":inconsistencyId" element={<InconsistencyPage />} />
                                </Route>

                                {/* New Kim */}
                                <Route path="new-kim/*" element={<NewKim />} />

                                {getFrontendURLRegion() !== 'au' && (
                                  <Route path="realtime">
                                    <Route index element={<Realtime />} />
                                    <Route path=":transcriptId" element={<Transcript />} />
                                  </Route>
                                )}

                                <Route path="entities" element={<Entities />} />
                              </Route>
                            </Route>
                          </Route>
                        </Route>

                        <Route path="*" element={<RedirectToNewFrontend />} />
                      </Route>
                    </Route>
                  </Routes>
                </TooltipProvider>
              </BrowserRouter>
            </MfaNonSSOGate>
          </UserProvider>
        </QueryClientProvider>
      </RequiredAuthProvider>
    </ErrorBoundary>
  );
}

export default App;
